import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn';
import Card from './Card';
import ProgressBar from './ProgressBar';

function useStatusConfig() {
  const { t } = useTranslation();
  return {
    completed: {
      badge: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      icon: '\u2713',
      label: t('common.completed'),
    },
    in_progress: {
      badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      icon: '\u25D0',
      label: t('common.inProgress'),
    },
    locked: {
      badge: 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500',
      icon: '\uD83D\uDD12',
      label: t('common.locked'),
    },
    available: {
      badge: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
      icon: '\u25CB',
      label: t('common.available'),
    },
  };
}

export default function ActivityCard({
  id,
  title,
  subtitle,
  status = 'available',
  progress = 0,
  meta,
  onClick,
  className,
  featured = false,
}) {
  const { t } = useTranslation();
  const statusConfig = useStatusConfig();
  const config = statusConfig[status] || statusConfig.available;
  const isClickable = status !== 'locked';

  return (
    <Card
      className={cn(
        'group cursor-pointer',
        isClickable
          ? 'hover:shadow-[0_4px_16px_rgba(0,0,0,0.12)] hover:-translate-y-0.5'
          : 'opacity-60 cursor-not-allowed',
        featured && 'border-2 border-gray-900 dark:border-gray-100',
        className
      )}
      gradient={featured}
      onClick={isClickable ? onClick : undefined}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className={cn(
            'font-semibold text-gray-900 dark:text-white',
            featured ? 'text-lg' : 'text-base'
          )}>
            {title}
          </h3>
          {subtitle && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>
          )}
          {meta && meta.length > 0 && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
              {meta.join(' · ')}
            </p>
          )}
        </div>
        <span
          className={cn(
            'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium',
            config.badge
          )}
        >
          <span>{config.icon}</span>
          <span>{config.label}</span>
        </span>
      </div>

      {(status === 'in_progress' || status === 'completed') && (
        <div className="mt-4">
          <ProgressBar
            value={progress}
            max={100}
            size="sm"
            variant={status === 'completed' ? 'success' : 'default'}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {status === 'completed' ? t('common.complete') : `${progress}% ${t('common.complete')}`}
          </p>
        </div>
      )}

      {featured && isClickable && (
        <div className="mt-4 flex justify-end">
          <span className="inline-flex items-center text-sm font-medium text-gray-900 dark:text-white group-hover:translate-x-1 transition-transform duration-200">
            {t('common.continue')}
            <svg
              className="ml-1 w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </span>
        </div>
      )}
    </Card>
  );
}

export function ActivityCardCompact({
  id,
  title,
  status = 'available',
  progress = 0,
  onClick,
  className,
}) {
  const statusConfig = useStatusConfig();
  const config = statusConfig[status] || statusConfig.available;
  const isClickable = status !== 'locked';

  return (
    <div
      className={cn(
        'rounded-lg border border-gray-200 dark:border-gray-700 p-4 transition-all duration-200',
        isClickable
          ? 'cursor-pointer hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-[0_2px_8px_rgba(0,0,0,0.08)]'
          : 'opacity-60 cursor-not-allowed',
        className
      )}
      onClick={isClickable ? onClick : undefined}
    >
      <div className="flex items-center justify-between">
        <span className="font-medium text-gray-900 dark:text-white text-sm">{title}</span>
        <span className={cn('text-xs px-2 py-0.5 rounded-full', config.badge)}>
          {config.icon} {config.label}
        </span>
      </div>
      {status === 'in_progress' && (
        <ProgressBar value={progress} max={100} size="sm" className="mt-2" />
      )}
    </div>
  );
}
